import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock } from '@focuslee/shared-mini-app';
import { RitualType, SubTask, SubTaskType } from '../client/generated/graphql';
import { updateQuestMutation } from '../client/mutations/updateQuest';
import { Page } from '../components/Page';
import { CustomInput } from '../components/common/CustomInput';
import { TaskTypeSelect } from '../components/common/TaskTypeSelect';
import { RitualTaskEditPageSkeleton } from '../components/ritual/skeleton/RitualTaskEditPageSkeleton';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';
import { useCachedRitualQuestIds } from '../hooks/caching/user';

interface TaskFormData {
    name: string;
    taskType: SubTaskType;
    promptText: string;
}

const defaultTaskForm: TaskFormData = {
    name: '',
    taskType: SubTaskType.Checkbox,
    promptText: '',
};

export const RitualTaskEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ritualType: ritualTypeParam, taskIndex: taskIndexParam } = useParams<{ ritualType: string; taskIndex?: string }>();
    const ritualType = ritualTypeParam as RitualType;
    const taskIndex = taskIndexParam ? parseInt(taskIndexParam, 10) : null;
    const isEditing = taskIndex !== null && !isNaN(taskIndex);
    
    const [taskForm, setTaskForm] = useState<TaskFormData>(defaultTaskForm);

    const { morningRitualId, eveningRitualId, loading: userLoading } = useCachedRitualQuestIds();
    const ritualId = ritualType === RitualType.Morning ? morningRitualId : eveningRitualId;
    const { quest: ritual, loading: ritualLoading } = useCachedQuestWithFallback(ritualId);
    const [updateQuest, { loading: saving }] = useMutation(updateQuestMutation);

    useEffect(() => {
        if (isEditing && ritual?.subTasks?.[taskIndex]) {
            const task = ritual.subTasks[taskIndex];
            setTaskForm({
                name: task.name,
                taskType: task.taskType ?? SubTaskType.Checkbox,
                promptText: task.promptText || '',
            });
        } else {
            setTaskForm(defaultTaskForm);
        }
    }, [isEditing, taskIndex, ritual]);

    const handleSave = useCallback(async () => {
        if (!ritual || !taskForm.name.trim()) return;
        if (taskForm.taskType === SubTaskType.Journal && !taskForm.promptText.trim()) return;

        const currentTasks = ritual.subTasks?.map(t => {
            const task: SubTask = {
                name: t.name,
                done: t.done,
                taskType: t.taskType,
            };
            if (t.promptText) {
                task.promptText = t.promptText;
            }
            if (t.journalEntryId) {
                task.journalEntryId = t.journalEntryId;
            }
            if (t.ratingEntryId) {
                task.ratingEntryId = t.ratingEntryId;
            }
            return task;
        }) ?? [];

        const newTask: SubTask = {
            name: taskForm.name.trim(),
            done: false,
            taskType: taskForm.taskType,
        };
        if (taskForm.taskType === SubTaskType.Journal) {
            newTask.promptText = taskForm.promptText.trim();
        }

        let updatedTasks;
        if (isEditing && taskIndex !== null) {
            updatedTasks = currentTasks.map((t, i) => {
                if (i === taskIndex) {
                    const updated: SubTask = {
                        name: newTask.name,
                        done: t.done,
                        taskType: newTask.taskType,
                    };
                    if (newTask.taskType === SubTaskType.Journal) {
                        updated.promptText = taskForm.promptText.trim();
                    }
                    return updated;
                }
                return {
                    name: t.name,
                    done: t.done,
                    taskType: t.taskType,
                    ...(t.promptText && { promptText: t.promptText }),
                    ...(t.journalEntryId && { journalEntryId: t.journalEntryId }),
                    ...(t.ratingEntryId && { ratingEntryId: t.ratingEntryId }),
                };
            });
        } else {
            updatedTasks = [...currentTasks, newTask];
        }

        await updateQuest({
            variables: {
                questId: ritual.id,
                name: ritual.name,
                description: ritual.description,
                categoryId: ritual.categoryId,
                difficulty: ritual.difficulty,
                goalId: ritual.goalId ?? undefined,
                timing: ritual.timing ?? false,
                timedInterval: ritual.timedInterval ?? 0,
                timerValueInMs: ritual.timerValueInMs ?? 0,
                repeat: ritual.repeat ? {
                    query: ritual.repeat.query,
                    repeatingType: ritual.repeat.repeatingType,
                    interval: ritual.repeat.interval ?? undefined,
                    weekDays: ritual.repeat.weekDays ?? [],
                    monthDays: ritual.repeat.monthDays ?? [],
                    startHour: ritual.repeat.startHour ?? undefined,
                    startMinute: ritual.repeat.startMinute ?? undefined,
                } : undefined,
                subTasks: updatedTasks,
            },
        });

        navigate(-1);
    }, [ritual, taskForm, isEditing, taskIndex, updateQuest]);

    const handleCancel = useCallback(() => {
        navigate(-1);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!ritual || !isEditing || taskIndex === null) return;

        const updatedTasks = ritual.subTasks
            ?.filter((_, i) => i !== taskIndex)
            .map(t => {
                const task: SubTask = {
                    name: t.name,
                    done: t.done,
                    taskType: t.taskType,
                };
                if (t.promptText) {
                    task.promptText = t.promptText;
                }
                if (t.journalEntryId) {
                    task.journalEntryId = t.journalEntryId;
                }
                if (t.ratingEntryId) {
                    task.ratingEntryId = t.ratingEntryId;
                }
                return task;
            }) ?? [];

        await updateQuest({
            variables: {
                questId: ritual.id,
                name: ritual.name,
                description: ritual.description,
                categoryId: ritual.categoryId,
                difficulty: ritual.difficulty,
                goalId: ritual.goalId ?? undefined,
                timing: ritual.timing ?? false,
                timedInterval: ritual.timedInterval ?? 0,
                timerValueInMs: ritual.timerValueInMs ?? 0,
                repeat: ritual.repeat ? {
                    query: ritual.repeat.query,
                    repeatingType: ritual.repeat.repeatingType,
                    interval: ritual.repeat.interval ?? undefined,
                    weekDays: ritual.repeat.weekDays ?? [],
                    monthDays: ritual.repeat.monthDays ?? [],
                    startHour: ritual.repeat.startHour ?? undefined,
                    startMinute: ritual.repeat.startMinute ?? undefined,
                } : undefined,
                subTasks: updatedTasks,
            },
        });

        navigate(-1);
    }, [ritual, isEditing, taskIndex, updateQuest]);

    const isValid = taskForm.name.trim().length > 0 &&
        (taskForm.taskType !== SubTaskType.Journal || taskForm.promptText.trim().length > 0);

    const isLoading = userLoading || ritualLoading;

    if (isLoading || !ritual) {
        return <RitualTaskEditPageSkeleton />;
    }

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: handleCancel, navigation: 'back' }}
                    main={{
                        text: isEditing ? t('actions.save') : t('ritual.addTask'),
                        onClick: handleSave,
                        enabled: isValid && !saving,
                        loading: saving,
                        bgColor: getButtonThemeColors(isValid && !saving).backgroundColor,
                    }}
                />
            }
        >
            <Stack height="100%" sx={{ background: 'var(--tgui--secondary_bg_color)' }}>
                <Box
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 'var(--tg-viewport-content-safe-area-inset-top)',
                        display: 'flex',
                    }}
                >
                    <Text>{isEditing ? t('ritual.editingTask') : t('ritual.addingTask')}</Text>
                </Box>
                <Stack height="100%" paddingLeft="10px" paddingRight="10px" paddingTop="4px" gap={1}>
                    <CustomInput
                        value={taskForm.name}
                        onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('ritual.taskName')}
                        style={{ fontSize: '20px', lineHeight: '20px' }}
                    />
                    <TaskTypeSelect
                        taskType={taskForm.taskType}
                        setTaskType={(taskType) => setTaskForm(prev => ({ ...prev, taskType }))}
                    />
                    {taskForm.taskType === SubTaskType.Journal && (
                        <CustomInput
                            value={taskForm.promptText}
                            onChange={(e) => setTaskForm(prev => ({ ...prev, promptText: e.target.value }))}
                            placeholder={t('ritual.prompt')}
                            style={{ fontSize: '16px', lineHeight: '20px' }}
                        />
                    )}
                    {isEditing && (
                        <Box
                            onClick={handleDelete}
                            sx={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                textAlign: 'center',
                            }}
                        >
                            <Text style={{ color: 'var(--tg-theme-destructive-text-color)', fontSize: '16px' }}>
                                {t('ritual.deleteTask')}
                            </Text>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Page>
    );
};
